
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageLayout } from '@/components/layout';
import MealCompositionForm from '@/components/meal-composition/MealCompositionForm';
import CompositionSuccessDialog from '@/components/meal-composition/CompositionSuccessDialog';
import { mealCompositionService } from '@/services/mealComposition';
import { useCompositionRecipes } from '@/hooks/useCompositionRecipes';
import { MEAL_COMPOSITIONS } from '@/constants/queryKeys';

interface CompositionSuccessData {
  id: string;
  title: string;
}

const CreateComposition = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successData, setSuccessData] = useState<CompositionSuccessData | null>(null);

  const { recipes } = useCompositionRecipes();

  const createMutation = useMutation({
    mutationFn: mealCompositionService.createMealComposition,
    onSuccess: (savedComposition, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEAL_COMPOSITIONS] });
      setSuccessData({
        id: savedComposition.id,
        title: variables.name
      });
    },
    onError: () => {
      console.error('Failed to create composition');
    },
  });

  const handleCreateSubmit = async (data: any) => {
    await createMutation.mutateAsync(data);
  };

  const handleSuccessDialogClose = () => {
    setSuccessData(null);
  };

  return (
    <PageLayout 
      title="Create Meal Composition"
      description="Group recipes together for easy meal planning"
    >
      <MealCompositionForm
        recipes={recipes}
        onSubmit={handleCreateSubmit}
        onCancel={() => navigate(-1)}
        isLoading={createMutation.isPending}
      />
      
      {successData && (
        <CompositionSuccessDialog
          isOpen={true}
          onOpenChange={handleSuccessDialogClose}
          compositionTitle={successData.title}
          compositionId={successData.id}
          isEditing={false}
        />
      )}
    </PageLayout>
  );
};

export default CreateComposition;
